/**
 * Presentational only. Parent should map API slots with utils/projectedLineupUtils (same as admin/www modal).
 */
import React from 'react';
import {
    Avatar,
    Box,
    Text,
    Flex,
    VStack,
    HStack,
    useColorModeValue,
} from '@chakra-ui/react';
import { getColor } from '../utils/colorUtils';
import TeamLogo from './TeamLogo';
import { httpsPlayerImageUrl } from '../utils/stringUtils';

/** Plain CSS media queries (Chakra `sx`) — viewport width in px */
const lineupSlotNumberSx = {
    '@media (max-width: 1499px)': { display: 'none' },
};
const lineupAvatarSx = {
    '@media (max-width: 1099px)': { display: 'none' },
};

interface TeamColors {
    primary: string;
    secondary: string;
}

/** Minimal team info for panel header: avatar + name + "Lineup" */
interface TeamInfoForHeader {
    name: string;
    logo?: string | null;
    color_primary?: string | null;
    color_secondary?: string | null;
    color_text?: string | null;
}

interface ProjectedLineupRow {
    name?: string;
    position?: string;
    ba?: number;
    oba?: number;
    slg?: number;
    first_name?: string;
    last_name?: string;
    image?: string;
}

interface ProjectedLineupProps {
    awayAbbr: string;
    homeAbbr: string;
    awayTeamColor?: string | null;
    homeTeamColor?: string | null;
    awayTeamInfo?: TeamInfoForHeader | null;
    homeTeamInfo?: TeamInfoForHeader | null;
    /** Placeholder lineup slots; when real data exists, pass player + stats info */
    awayLineup?: ProjectedLineupRow[];
    homeLineup?: ProjectedLineupRow[];
    awayStarter?: string;
    homeStarter?: string;
}

function formatBattingAvg(avg?: number): string {
    const value = typeof avg === 'number' ? avg : 0;
    const fixed = value.toFixed(3);
    return value < 1 ? fixed.replace(/^0/, '') : fixed;
}

function LineupPlayerAvatar({
    row,
    teamInfo,
}: {
    row: ProjectedLineupRow;
    teamInfo?: TeamInfoForHeader | null;
}) {
    const bg = getColor(teamInfo?.color_primary ?? null, '#4A5568');
    const text = getColor(teamInfo?.color_text ?? null, '#FFFFFF');
    const border = getColor(teamInfo?.color_secondary ?? null, 'transparent');

    const fullName =
        (row.first_name && row.last_name && `${row.first_name} ${row.last_name}`) || row.name || '';

    if (httpsPlayerImageUrl(row.image)) {
        return (
            <Box
                w="40px"
                h="40px"
                borderRadius="full"
                overflow="hidden"
                bg="gray.600"
                flexShrink={0}
            >
                <img
                    src={httpsPlayerImageUrl(row.image)}
                    alt={fullName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={(e) => {
                        e.currentTarget.style.display = 'none';
                    }}
                />
            </Box>
        );
    }

    // Fallback avatar with initials like RosterContent
    const initials =
        (row.first_name && row.last_name && `${row.first_name[0]}${row.last_name[0]}`.toUpperCase()) ||
        (fullName ? fullName.slice(0, 2).toUpperCase() : '');

    return (
        <Avatar
            size="sm"
            name={fullName}
            bg={bg}
            color={text}
            border={`2px solid ${border}`}
        >
            {initials}
        </Avatar>
    );
}

function LineupPanel({
    teamAbbr,
    teamInfo,
    teamColor,
    lineup,
    likelyStarter,
    side,
}: {
    teamAbbr: string;
    teamInfo?: TeamInfoForHeader | null;
    teamColor: TeamColors;
    lineup: ProjectedLineupRow[];
    likelyStarter?: string;
    side: 'away' | 'home';
}) {
    const rows = Array.from({ length: 9 }, (_, i) => lineup[i] ?? {});
    const teamName = teamInfo?.name ?? teamAbbr;
    // Use the team's configured text color from myLeague (no extra heuristics).
    const textColor = getColor(teamInfo?.color_text ?? null, '#ffffff');

    return (
        <VStack
            align="stretch"
            spacing={0}
            flex={1}
            minW={0}
            bg={teamColor.primary}
            borderRadius="lg"
            p={4}
            color={textColor}
        >
            <Box
                mb={2}
                px={2}
                py={1}
                borderRadius="md"
                bgGradient={`linear(to-r, #1A202C 0%, ${teamColor.primary} 100%)`}
            >
                <HStack spacing={2} align="center">
                    {teamInfo ? (
                        <TeamLogo team={teamInfo} size="sm" />
                    ) : (
                        <Box w={9} h={9} borderRadius="full" bg="whiteAlpha.300" flexShrink={0} />
                    )}
                    <Text fontWeight="bold" fontSize="lg">
                        {teamName} Lineup
                    </Text>
                </HStack>
            </Box>
            <Flex justify="flex-end" mb={2} px={1}>
                <HStack spacing={2} minW="126px" justify="flex-end">
                    <Text fontSize="xs" fontWeight="bold" minW="38px" textAlign="right">BA</Text>
                    <Text fontSize="xs" fontWeight="bold" minW="38px" textAlign="right">OBP</Text>
                    <Text fontSize="xs" fontWeight="bold" minW="38px" textAlign="right">SLG</Text>
                </HStack>
            </Flex>
            <VStack align="stretch" spacing={3}>
                {rows.map((row, idx) => (
                    <Flex key={idx} align="center" gap={2}>
                        <Flex
                            w={7}
                            h={7}
                            borderRadius="full"
                            bg="whiteAlpha.300"
                            align="center"
                            justify="center"
                            flexShrink={0}
                            fontSize="sm"
                            fontWeight="bold"
                            sx={lineupSlotNumberSx}
                        >
                            {idx + 1}
                        </Flex>
                        <Box flexShrink={0} sx={lineupAvatarSx}>
                            <LineupPlayerAvatar row={row} teamInfo={teamInfo} />
                        </Box>
                        <Box flex={1} minW={0} textAlign="left">
                            <Text fontSize="sm" fontWeight="bold" noOfLines={1}>
                                {row.name || '—'}
                            </Text>
                            <Text fontSize="xs" opacity={0.9}>
                                {row.position || '—'}
                            </Text>
                        </Box>
                        <HStack spacing={2} minW="126px" justify="flex-end">
                            <Text fontSize="xs" fontWeight="medium" minW="38px" textAlign="right">{formatBattingAvg(row.ba)}</Text>
                            <Text fontSize="xs" fontWeight="medium" minW="38px" textAlign="right">{formatBattingAvg(row.oba)}</Text>
                            <Text fontSize="xs" fontWeight="medium" minW="38px" textAlign="right">{formatBattingAvg(row.slg)}</Text>
                        </HStack>
                    </Flex>
                ))}
            </VStack>
            <Box h={4} />
            <Text fontSize="sm" opacity={0.95}>
                Likely Starter: {likelyStarter || 'Unknown'}
            </Text>
        </VStack>
    );
}

/**
 * Displays two starting lineups (away on left, home on right) with YTD batting rates.
 * Team-colored panels, batting order 1–9 with name/position and BA/OBA/SLG columns,
 * then Likely Starter. Accepts optional lineup/starter data; otherwise shows placeholders.
 */
const ProjectedLineup: React.FC<ProjectedLineupProps> = ({
    awayAbbr,
    homeAbbr,
    awayTeamColor,
    homeTeamColor,
    awayTeamInfo,
    homeTeamInfo,
    awayLineup = [],
    homeLineup = [],
    awayStarter,
    homeStarter,
}) => {
    const defaultPrimary = useColorModeValue('#553C9A', '#44337A');
    const defaultPrimaryHome = useColorModeValue('#2B6CB0', '#2C5282');

    const awayColors: TeamColors = {
        primary: getColor(awayTeamColor ?? undefined, defaultPrimary),
        secondary: 'rgba(255,255,255,0.3)',
    };
    const homeColors: TeamColors = {
        primary: getColor(homeTeamColor ?? undefined, defaultPrimaryHome),
        secondary: 'rgba(255,255,255,0.3)',
    };

    return (
        <Flex gap={4} align="stretch" direction={{ base: 'column', md: 'row' }}>
            <LineupPanel
                side="away"
                teamAbbr={awayAbbr}
                teamInfo={awayTeamInfo}
                teamColor={awayColors}
                lineup={awayLineup}
                likelyStarter={awayStarter}
            />
            <LineupPanel
                side="home"
                teamAbbr={homeAbbr}
                teamInfo={homeTeamInfo}
                teamColor={homeColors}
                lineup={homeLineup}
                likelyStarter={homeStarter}
            />
        </Flex>
    );
};

export default ProjectedLineup;
